import { useEffect, useMemo, useRef, useState } from 'react';
import { getImageUrl, getPropertyImage } from '../utils/imageHelper';
import { formatPrice as formatPriceUtil } from '../utils/currencyPreference';
import { getHotelCoordinates } from '../utils/mapCoordinates';
import { fetchGeocode, spreadCoordinate } from '../utils/meetingMapGeocode';
import { buildHotelGeocodeQuery, spreadOverlappingCoordinates } from '../utils/hotelMapGeocode';

export const DEFAULT_MAP_CENTER = { lat: 17.385, lng: 78.4867 };

const GEOCODE_BATCH_SIZE = 3;

const buildPriceLabel = (hotel, checkIn, checkOut) => {
  const price = hotel.pricePerNight || 0;
  const currency = hotel.currency || 'INR';
  let nights = 1;
  if (checkIn && checkOut) {
    const ms = checkOut.getTime() - checkIn.getTime();
    if (ms > 0) nights = Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
  }
  const total = price * nights;
  if (total > 0) {
    return formatPriceUtil(total, currency).replace(/\s/g, ' ').slice(0, 14);
  }
  if (price > 0) {
    return formatPriceUtil(price, currency).replace(/\s/g, ' ').slice(0, 14);
  }
  return '—';
};

const buildPinFromHotel = (hotel, coords, checkIn, checkOut) => {
  const id = hotel._id || hotel.id;
  return {
    hotel,
    id,
    lat: coords.lat,
    lng: coords.lng,
    priceLabel: buildPriceLabel(hotel, checkIn, checkOut),
    image: getPropertyImage(hotel) || getImageUrl(hotel.images?.[0]),
    name: hotel.name || 'Property',
    rating: hotel.rating,
  };
};

const resolveMissingCoords = async (hotel, search, center, fallbackIndex, total) => {
  const query = buildHotelGeocodeQuery(hotel, search);
  let coords = await fetchGeocode(query);
  if (!coords && search) {
    coords = await fetchGeocode(`${hotel.name || 'Hotel'}, ${search}, India`);
  }
  if (!coords) {
    coords = spreadCoordinate(center.lat, center.lng, fallbackIndex, total);
  }
  return coords;
};

export const useHotelMapPins = ({
  hotels = [],
  searchQuery = '',
  checkIn = null,
  checkOut = null,
  defaultCenter = DEFAULT_MAP_CENTER,
  searchCenter = null,
  onPinsChange = null,
}) => {
  const [pins, setPins] = useState([]);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [geocoding, setGeocoding] = useState(false);
  const onPinsChangeRef = useRef(onPinsChange);
  onPinsChangeRef.current = onPinsChange;

  const hotelsKey = useMemo(
    () => hotels.map((h) => `${h._id || h.id}-${h.pricePerNight}-${h.city || ''}`).join('|'),
    [hotels]
  );

  useEffect(() => {
    let cancelled = false;

    const publishPins = (nextPins) => {
      const spread = spreadOverlappingCoordinates(nextPins);
      setPins(spread);
      onPinsChangeRef.current?.(spread);
    };

    const loadPins = async () => {
      if (!hotels.length) {
        setPins([]);
        setGeocoding(false);
        return;
      }

      setGeocoding(true);
      const search = String(searchQuery || '').trim();

      let center = searchCenter || defaultCenter;
      if (!searchCenter && search) {
        const cityCoords = await fetchGeocode(`${search}, India`);
        if (cityCoords) center = cityCoords;
      }
      if (!cancelled) setMapCenter(center);

      const resolved = [];
      const needGeocode = [];

      hotels.forEach((hotel) => {
        const coords = getHotelCoordinates(hotel);
        if (coords) {
          resolved.push(buildPinFromHotel(hotel, coords, checkIn, checkOut));
        } else {
          needGeocode.push(hotel);
        }
      });

      if (!cancelled && resolved.length) {
        publishPins(resolved);
      }

      if (!needGeocode.length) {
        if (!cancelled) setGeocoding(false);
        return;
      }

      let fallbackIndex = 0;
      for (let i = 0; i < needGeocode.length; i += GEOCODE_BATCH_SIZE) {
        if (cancelled) return;
        const batch = needGeocode.slice(i, i + GEOCODE_BATCH_SIZE);
        const batchPins = await Promise.all(
          batch.map(async (hotel) => {
            const coords = await resolveMissingCoords(
              hotel,
              search,
              center,
              fallbackIndex,
              needGeocode.length
            );
            fallbackIndex += 1;
            return buildPinFromHotel(hotel, coords, checkIn, checkOut);
          })
        );
        resolved.push(...batchPins);
        if (!cancelled) publishPins(resolved);
      }

      if (!cancelled) setGeocoding(false);
    };

    loadPins();
    return () => {
      cancelled = true;
    };
  }, [hotelsKey, searchQuery, defaultCenter.lat, defaultCenter.lng, checkIn, checkOut, hotels.length, searchCenter?.lat, searchCenter?.lng]);

  return { pins, mapCenter, geocoding };
};
